import React, { useState, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import "./VisualManagement1.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function VisualManagement1() {
  const history = useHistory();
  const [hasImplemented, setHasImplemented] = useState(null);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const [formData, setFormData] = useState({
    // Part 1: Current state - What is in place today
    // Objectives and intentions
    visualManagementObjectives: "",
    implementationTime: "",
    visualSupports: "",
    coveredAreas: "",
    designStandard: "",

    // Content of visuals
    displayedInformation: "",
    indicatorsUpdate: "",
    visualsComprehension: "",
    flowManagementVisuals: "",
    visualsMaintenance: "",

    // Part 2: Desired state - Target vision of visual management
    // Ambitions and vision
    expectedContributions: "",
    interactiveData: "",
    additionalInformation: "",
    operatorsAutonomy: "",
    additionalVisuals: "",

    // Part 3: Monitoring, ownership and continuous improvement
    // Management & reactivity
    indicatorsResponsible: "",
    fieldRituals: "",
    anomaliesDetection: "",
    escalationSystem: "",
    visualsReview: "",

    // Lean involvement and culture
    teamsParticipation: "",
    visualManagementTraining: "",
    visualsAdaptation: "",
    teamImpact: "",
    roadmap: ""
  });

  const questionnaireRef = useRef(null);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedFile) {
      setErrorMessage("Please select a file");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      console.log("Selected file:", selectedFile);
      setTimeout(() => {
        history.push("/visual-management-2");
        setIsLoading(false);
      }, 1500);
    } catch (error) {
      console.error("Error while uploading the file:", error);
      setErrorMessage("An error occurred while uploading. Please try again.");
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleQuestionsSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    try {
      console.log("Questionnaire data:", formData);
      setTimeout(() => {
        history.push("/visual-management-2");
        setIsLoading(false);
      }, 1500);
    } catch (error) {
      console.error("Error sending questionnaire data:", error);
      setErrorMessage("An error occurred while sending the form. Please try again.");
      setIsLoading(false);
    }
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    }
  };

  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>1</div>
        <div className="progress-bar-line"></div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>2</div>
        <div className="progress-bar-line"></div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
      </div>
    );
  };

  const renderQuestionnaire = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 1 : Current state - What is in place today</h2>

            <div className="question-group">
              <h3 className="question-group-title">Objectives and intentions</h3>

              <div className="form-group">
                <label htmlFor="visualManagementObjectives">
                  What are the objectives sought through the use of visual management (performance monitoring, safety, flow, communicationâ€¦)?
                </label>
                <textarea
                  id="visualManagementObjectives"
                  name="visualManagementObjectives"
                  value={formData.visualManagementObjectives}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe the objectives sought..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="implementationTime">
                  Since when has visual management been in place in your organization?
                </label>
                <textarea
                  id="implementationTime"
                  name="implementationTime"
                  value={formData.implementationTime}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Specify the implementation period..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="visualSupports">
                  What types of visual supports are currently used (boards, screens, floor markings, QR codes, LED, etc.)?
                </label>
                <textarea
                  id="visualSupports"
                  name="visualSupports"
                  value={formData.visualSupports}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="List the visual supports used..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="coveredAreas">
                  Which processes or areas are covered by visual management (production, logistics, quality, offices, etc.)?
                </label>
                <textarea
                  id="coveredAreas"
                  name="coveredAreas"
                  value={formData.coveredAreas}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe the areas covered..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="designStandard">
                  Do you have a design standard or a visual charter common to the company?
                </label>
                <textarea
                  id="designStandard"
                  name="designStandard"
                  value={formData.designStandard}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe your design standards..."
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">Content of visuals</h3>

              <div className="form-group">
                <label htmlFor="displayedInformation">
                  What types of information are displayed (indicators, planning, safety, incidents, quality, production)?
                </label>
                <textarea
                  id="displayedInformation"
                  name="displayedInformation"
                  value={formData.displayedInformation}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="List the types of displayed information..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="indicatorsUpdate">
                  Are the indicators updated in real time, manually or automatically?
                </label>
                <textarea
                  id="indicatorsUpdate"
                  name="indicatorsUpdate"
                  value={formData.indicatorsUpdate}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe the update process..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="visualsComprehension">
                  Are visual supports understandable by all staff without explanation?
                </label>
                <textarea
                  id="visualsComprehension"
                  name="visualsComprehension"
                  value={formData.visualsComprehension}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Evaluate the understanding of visuals..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="flowManagementVisuals">
                  Do you have visuals for physical flow management (pull flow, Kanban zones, WIP, etc.)?
                </label>
                <textarea
                  id="flowManagementVisuals"
                  name="flowManagementVisuals"
                  value={formData.flowManagementVisuals}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe your flow management visuals..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="visualsMaintenance">
                  Are the visual supports regularly maintained, organized, cleaned, and relevant?
                </label>
                <textarea
                  id="visualsMaintenance"
                  name="visualsMaintenance"
                  value={formData.visualsMaintenance}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe the maintenance of your visual supports..."
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 2 : Desired state - Target vision of visual management</h2>

            <div className="question-group">
              <h3 className="question-group-title">Ambitions and vision</h3>

              <div className="form-group">
                <label htmlFor="expectedContributions">
                  What would be, according to you, the expected contributions of a fully deployed visual management system?
                </label>
                <textarea
                  id="expectedContributions"
                  name="expectedContributions"
                  value={formData.expectedContributions}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe the expected contributions..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="interactiveData">
                  Would you like to make your data more interactive (dynamic screens, connected displays, digital andonâ€¦)?
                </label>
                <textarea
                  id="interactiveData"
                  name="interactiveData"
                  value={formData.interactiveData}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe your interactivity wishes..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="additionalInformation">
                  What types of additional information would you like to see displayed visually?
                </label>
                <textarea
                  id="additionalInformation"
                  name="additionalInformation"
                  value={formData.additionalInformation}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="List desired additional information..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="operatorsAutonomy">
                  Are you aiming for increased autonomy for operators or technicians through the use of visuals?
                </label>
                <textarea
                  id="operatorsAutonomy"
                  name="operatorsAutonomy"
                  value={formData.operatorsAutonomy}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe your autonomy objectives..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="additionalVisuals">
                  Do you want to integrate more visuals related to safety, quality, or field performance?
                </label>
                <textarea
                  id="additionalVisuals"
                  name="additionalVisuals"
                  value={formData.additionalVisuals}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Specify desired additional visuals..."
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 3 : Monitoring, ownership and continuous improvement</h2>

            <div className="question-group">
              <h3 className="question-group-title">Management & reactivity</h3>

              <div className="form-group">
                <label htmlFor="indicatorsResponsible">
                  Who is responsible for updating and monitoring the visual indicators?
                </label>
                <textarea
                  id="indicatorsResponsible"
                  name="indicatorsResponsible"
                  value={formData.indicatorsResponsible}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Identify the responsible people..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="fieldRituals">
                  Do you use visual supports to conduct field rituals (TOP 5, Gemba, safety briefingâ€¦)?
                </label>
                <textarea
                  id="fieldRituals"
                  name="fieldRituals"
                  value={formData.fieldRituals}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe your field rituals..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="anomaliesDetection">
                  Are deviations or anomalies visible and easily detectable through visual management?
                </label>
                <textarea
                  id="anomaliesDetection"
                  name="anomaliesDetection"
                  value={formData.anomaliesDetection}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Evaluate anomaly visibility..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="escalationSystem">
                  Do you have an escalation or rapid resolution system based on visuals (traffic lights, andon, QRQCâ€¦)?
                </label>
                <textarea
                  id="escalationSystem"
                  name="escalationSystem"
                  value={formData.escalationSystem}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe your escalation system..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="visualsReview">
                  How often are the visuals reviewed, adjusted, or removed if they are no longer relevant?
                </label>
                <textarea
                  id="visualsReview"
                  name="visualsReview"
                  value={formData.visualsReview}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Specify the review frequency..."
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">Lean involvement and culture</h3>

              <div className="form-group">
                <label htmlFor="teamsParticipation">
                  Do field teams participate in the creation or improvement of visual supports?
                </label>
                <textarea
                  id="teamsParticipation"
                  name="teamsParticipation"
                  value={formData.teamsParticipation}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe team participation..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="visualManagementTraining">
                  Has any training or awareness on visual management been implemented?
                </label>
                <textarea
                  id="visualManagementTraining"
                  name="visualManagementTraining"
                  value={formData.visualManagementTraining}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe your visual management training..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="visualsAdaptation">
                  Is visual management adapted to different profiles (languages, literacy, roles)?
                </label>
                <textarea
                  id="visualsAdaptation"
                  name="visualsAdaptation"
                  value={formData.visualsAdaptation}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Evaluate adaptation to profiles..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="teamImpact">
                  Do you observe a real impact of visuals on team engagement or responsiveness?
                </label>
                <textarea
                  id="teamImpact"
                  name="teamImpact"
                  value={formData.teamImpact}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe the observed impact..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="roadmap">
                  What is your roadmap to develop or strengthen the visual culture in the next 6 to 12 months?
                </label>
                <textarea
                  id="roadmap"
                  name="roadmap"
                  value={formData.roadmap}
                  onChange={handleInputChange}
                  required
                  className="form-textarea"
                  placeholder="Describe your roadmap..."
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsGeneratingPDF(true);

      const element = document.querySelector(".visual-management-page");
      if (!element) {
        alert("Element not found!");
        return;
      }

      document.querySelectorAll("*").forEach((el) => {
        const style = window.getComputedStyle(el);

        if (style.backgroundImage && style.backgroundImage.includes("gradient")) {
          el.style.backgroundImage = "none";
        }

        if (
          style.backgroundColor === "transparent" ||
          style.backgroundColor === "rgba(0, 0, 0, 0)" ||
          style.backgroundColor.includes("var(")
        ) {
          el.style.backgroundColor = "#ffffff";
        }
      });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: -window.scrollY,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        onclone: (clonedDoc) => {
          clonedDoc.querySelectorAll("*").forEach((el) => {
            const position = window.getComputedStyle(el).position;
            if (position === "sticky" || position === "fixed") {
              el.style.position = "static";
            }
          });
        },
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`VisualManagement_${date}.pdf`);

      alert("âœ… PDF generated successfully!");
    } catch (err) {
      console.error("PDF Error:", err);
      alert("âŒ PDF Error: " + (err.message || "unknown"));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const saveLeanAnswers = async (moduleName) => {
    try {
      const caseId = localStorage.getItem("caseId");
      if (!caseId) {
        alert("No active case found!");
        console.warn("LocalStorage is empty. Current content:", localStorage);
        return;
      }

      console.log(`Saving ${moduleName} answers for case ${caseId}`);

      const API_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_URL}/api/lean/${moduleName}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          case_id: caseId,
          answers: formData,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || `Saving error (${response.status}) : ${result.message}`);
      }

      alert(`âœ… ${moduleName.toUpperCase()} answers saved successfully!`);

      handleNextStep();

    } catch (error) {
      console.error("âŒ saveLeanAnswers Error:", error);
      alert("âŒ " + error.message);
    }
  };

  return (
    <div className="container-center-horizontal">
      <div className="visual-management-page screen">
        <div className="header">
          <Link to="/home">
            <img
              className="logo-icam"
              src="/img/1200px-logo-icam---2008-1@2x.png"
              alt="Logo ICAM"
            />
          </Link>
        </div>

        <div className="content-container">
          <h1 className="page-title">Visual Management - Effective Communication</h1>

          <div className="question-section">
            <p className="question-text">
              Has your company already implemented Visual Management?
            </p>

            <div className="buttons-container">
              <button
                className={`choice-button ${hasImplemented === true ? 'active' : ''}`}
                onClick={() => {
                  setHasImplemented(true);
                  setShowFileUpload(true);
                  setShowQuestionnaire(true);
                  setErrorMessage("");
                }}
              >
                Yes
              </button>
              <button
                className={`choice-button ${hasImplemented === false ? 'active' : ''}`}
                onClick={() => {
                  setHasImplemented(false);
                  setShowFileUpload(false);
                  setShowQuestionnaire(false);
                  setErrorMessage("");
                }}
              >
                No
              </button>
            </div>

            {errorMessage && (
              <div className="error-message">
                {errorMessage}
              </div>
            )}

            {showFileUpload && (
              <div className="file-upload-section">
                <p className="upload-text">
                  Please upload your existing Visual Management documentation:
                </p>
                <form onSubmit={handleSubmit} className="upload-form">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    className="file-input"
                  />
                  <button
                    type="submit"
                    className="submit-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : "Upload"}
                  </button>
                </form>
              </div>
            )}

            {showQuestionnaire && (
              <div className="questions-section" ref={questionnaireRef}>
                <h2 className="questions-title">Assessment of your current situation</h2>

                {renderProgressIndicator()}

                <form onSubmit={handleQuestionsSubmit} className="questions-form">
                  {renderQuestionnaire()}

                  <div className="form-actions">
                    {currentStep > 1 && (
                      <button
                        type="button"
                        className="nav-button prev-button"
                        onClick={handlePrevStep}
                      >
                        Previous
                      </button>
                    )}

                    {currentStep < 3 ? (
                      <button
                        type="button"
                        className="nav-button next-button"
                        onClick={() => saveLeanAnswers("visual-management")}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner"></span>
                            Sending...
                          </>
                        ) : "Finish"}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}

            {hasImplemented === false && (
              <div className="redirect-section">
                <p className="redirect-text">
                  We recommend that you start by understanding the principles of Visual Management:
                </p>
                <Link to="/pdf-uploader" className="redirect-button">
                  Go to saving
                </Link>
              </div>
            )}
          </div>
        </div>

        <button
          className="download-button"
          onClick={handleDownloadPDF}
          disabled={isGeneratingPDF}
        >
          {isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
        </button>
      </div>
    </div>
  );
}

export default VisualManagement1;
